import { useMemo, useState } from 'react';
import { GridView } from '@vivli/shared/views';
import { ResearchTeamMemberRoleEnum, VmAdminApprovalStatusEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { IDropdownMenuItem, IUser } from '@vivli/shared/infrastructure/interface';
import {
    IDataRequest,
    IResearchTeamMemberDetail,
    IVirtualMachineAccessInvitation
} from '@vivli/features/data-requests/infrastructure/interface';
import { EllipsisDropdownMenuComponent } from '@vivli/shared/components';
import { ColDef } from 'ag-grid-community';
import { GridCellRendererEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useResearchTeamCreate, useResearchTeamPermissions } from '@vivli/features/data-requests/infrastructure/hook';
import { finalize, first } from 'rxjs/operators';
import { IUserEmailParam } from '@vivli/features/users/infrastructure/interface';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { TeamMemberModalComponent } from './team-member-modal.component';
import { DraftTeamMemberModalComponent } from './draft-team-member-modal.component';
import { useFormContext } from 'react-hook-form';
import {
    useActiveUserContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useUsersApiContext
} from '@vivli/core/infrastructure/context';

interface ResearchTeamGridComponentProps {
    members: IResearchTeamMemberDetail[];
    draftMode: boolean;
    itemHasError?: boolean;
    onRemove?: (teamMember: IResearchTeamMemberDetail) => void;
    onGrantDuaApproval?: (teamMember: IResearchTeamMemberDetail) => void;
    onRevokeDuaApproval?: (teamMember: IResearchTeamMemberDetail) => void;
    onRevokeVmAccess?: (teamMember: IResearchTeamMemberDetail) => void;
    onInviteToVm?: (teamMember: IResearchTeamMemberDetail) => void;
    onMakeOwner?: (teamMember: IResearchTeamMemberDetail) => void;
    onCancelInvite?: (teamMember: IResearchTeamMemberDetail) => void;
    onSetAccessTraining?: (teamMember: IResearchTeamMemberDetail) => void;
    onChange?: (dataRequest: IDataRequest) => void;
}

export const ResearchTeamGridComponent = ({
    members,
    draftMode,
    onRemove,
    itemHasError,
    onGrantDuaApproval,
    onRevokeDuaApproval,
    onRevokeVmAccess,
    onInviteToVm,
    onMakeOwner,
    onCancelInvite,
    onChange
}: ResearchTeamGridComponentProps) => {
    const { dataRequest, isAdminEditing, isActiveUserOrgAdmin } = useDataRequestContext();
    // const [menuItems, setMenuItems] = useState<IDropdownMenuItem[]>();
    // const [subTitle, setSubTitle] = useState<string>(null);
    // const [userAccountStatus, setUserAccountStatus] = useState<string>();
    // const [isDaTrained, setUserDaTrained] = useState<string>();
    const { userCanEdit, userCanManage, userCanDelete } = useResearchTeamPermissions(dataRequest, isAdminEditing);
    const formApi = useFormContext();
    const arrayName = 'researchTeam.researchTeamDetails';
    const modalService = useModalApiContext();
    const { user, hasPermission } = useActiveUserContext();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);
    const createResearcher = useResearchTeamCreate();
    const getMemberArrayPath = (index: number) => `${arrayName}.${index}`;
    const dataRequestsService = useDataRequestsApiContext();
    const [isLoading, setIsLoading] = useState(false);
    const usersService = useUsersApiContext();

    const handleError = (e) => {
        modalService.message(e.message);
    };

    const handleChange = (dataRequest: IDataRequest) => {
        const updatedMember = dataRequest.researchTeam.researchTeamDetails.find((rtd) => rtd.teamMemberId === updatedMember.teamMemberId);
        onChange && onChange(dataRequest);
    };

    const handleCleanup = () => {
        setIsLoading(false);
    };

    const refreshScreen = () => {
        window.location.reload();
    };

    const setApprovalStatus = (isApprove: boolean, teamMember: IResearchTeamMemberDetail) => {
        const approvalVerb = isApprove ? 'grant' : 'deny';
        const confirmMsg = `Are you sure you want to ${approvalVerb} access to the data for ${teamMember.firstName} ${teamMember.lastName}?`;
        modalService.confirm(confirmMsg, {
            title: 'Set Data Access Approval',
            onConfirm: () => {
                setIsLoading(true);
                dataRequestsService
                    .setTeamMemberAdminApproval(dataRequest.id, teamMember, isApprove ? 'true' : 'false')
                    .pipe(first(), finalize(handleCleanup))
                    .subscribe(refreshScreen, handleError);

                // // Don't even THINK about using teamMember.statusArray in the forEach loop
                // let tempWorkArray = teamMember.statusArray;
                //
                // let itemFound = false;
                // let n = 0;
                // tempWorkArray.forEach((value) => {
                //     if ((value === 'Access to Data Denied' || value === 'Access to Data Pending') && isApprove) {
                //         tempWorkArray[n] = 'Access to Data Granted';
                //         itemFound = true;
                //         return teamMember;
                //     }
                //     if ((value === 'Access to Data Granted' || value === 'Access to Data Pending') && !isApprove) {
                //         tempWorkArray[n] = 'Access to Data Denied';
                //         itemFound = true;
                //         return teamMember;
                //     }
                //     n = n + 1;
                // });
                // if (!itemFound) {
                //     if (isApprove) {
                //         tempWorkArray?.push('Access to Data Granted');
                //     }
                //     if (!isApprove) {
                //         tempWorkArray?.push('Access to Data Denied');
                //     }
                // }
                // if (isApprove) {
                //     teamMember.adminApprovalStatus = VmAdminApprovalStatusEnum.Approved;
                // } else {
                //     teamMember.adminApprovalStatus = VmAdminApprovalStatusEnum.Disapproved;
                // }
                //
                // teamMember.statusArray = tempWorkArray;
                // getMenuItems(teamMember);
            }
        });
        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnRemove = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to remove "${teamMember.firstName} ${teamMember.lastName}"?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                onRemove(teamMember);
            }
        });
    };

    const handleRevokeAccessRequest = (requestMessage: string, dataRequest: IDataRequest, teamMember: IResearchTeamMemberDetail) => {
        modalService.confirm(requestMessage, {
            onConfirm: () => {
                setIsLoading(true);
                dataRequestsService
                    .revokeVmAccess(dataRequest.id, teamMember.teamMemberId)
                    .pipe(first(), finalize(handleCleanup))
                    .subscribe(refreshScreen, handleError);

                // let itemFound = false;
                //
                // const tempWorkArray = teamMember.statusArray.map((status) => {
                //     if (status === 'Access to Data Request Granted' || status === 'Access to Data Request Pending') {
                //         itemFound = true;
                //         return 'Access to Data Request Denied';
                //     }
                //
                //     if (status === 'Access to Data Request Granted for Admin') {
                //         itemFound = true;
                //         return status;
                //     }
                //
                //     return status;
                // });
                //
                // if (!itemFound) {
                //     tempWorkArray?.push('Access to Data Request Denied');
                // }
                //
                // teamMember.hasAccessToVirtualMachineResource = false;
                // getMenuItems(teamMember);
                // teamMember.statusArray = tempWorkArray;
            }
        });

        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnRevokeVmAccess = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to revoke access for "${teamMember.firstName} ${teamMember.lastName}"?`;
        handleRevokeAccessRequest(confirmMsg, dataRequest, teamMember);
    };

    const handleOnGrantDuaApproval = (teamMember) => {
        setApprovalStatus(true, teamMember);
        teamMember.adminApprovalStatus = 'Approved';
        getMenuItems(teamMember);
    };

    const handleOnRevokeDuaApproval = (teamMember) => {
        setApprovalStatus(false, teamMember);
        teamMember.adminApprovalStatus = 'Disapproved';
        getMenuItems(teamMember);
    };

    const handleOnCancelInvite = (teamMember) => {
        const confirmMsg = `Are you sure you want to cancel your invite for "${teamMember.firstName} ${teamMember.lastName}"?`;
        handleRevokeAccessRequest(confirmMsg, dataRequest, teamMember);
    };

    const handleOnMakeOwner = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to make "${teamMember.firstName} ${teamMember.lastName}" the Admin of the data request?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                setIsLoading(true);
                dataRequestsService
                    .changeDataRequestOwner(dataRequest.id, teamMember)
                    .pipe(first(), finalize(handleCleanup))
                    .subscribe(refreshScreen, handleError);
                // let itemFound = false;
                // const tempRolesArray = teamMember.rolesArray.map((role: string) => {
                //     if (role === 'Admin') {
                //         itemFound = true;
                //     }
                //     return role;
                // });
                //
                // if (!itemFound) {
                //     tempRolesArray.push('Admin');
                // }
                // teamMember.rolesArray = tempRolesArray;
            }
        });

        const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
            if (tm.id === teamMember.id) {
                return teamMember;
            }

            return tm;
        });
        //need also to find the previous admin and remove the role from that members list

        formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnSetUnSetAccessTraining = (isDaTrainingComplete, teamMember) => {
        onSetUnSetAccessTraining(isDaTrainingComplete, teamMember);
    };

    const onSetUnSetAccessTraining = (isDaTrainingComplete: boolean, teamMember: IResearchTeamMemberDetail) => {
        usersService.setUserDaTraining(teamMember.userId, isDaTrainingComplete).subscribe((result) => {
            refreshScreen();
            // const updatedRole = { ...user.userRole, isDaTrainingComplete };
            //
            // //UserRole now updated in Cosmos, now set the status array accordingly in the team member
            // // Return a new object with the updated value -  which needs to be the appropriate text, not the mere boolean
            // let tempWorkArray = teamMember.statusArray;
            // let n = 0;
            // tempWorkArray.forEach((value) => {
            //     if (value === 'Data Access Training Not Completed' && isDaTrainingComplete) {
            //         tempWorkArray[n] = 'Data Access Training Completed';
            //         return teamMember;
            //     }
            //     if (value === 'Data Access Training Completed' && !isDaTrainingComplete) {
            //         tempWorkArray[n] = 'Data Access Training Not Completed';
            //         return teamMember;
            //     }
            //     n = n + 1;
            // });
            //
            // let message = result; // result? result of update to UserRole
            // //update the Toaster message
            // if (isDaTrainingComplete) {
            //     if (result === 'Updated') {
            //         message = `Successfully set training status to completed for ${teamMember.firstName} ${teamMember.lastName}`;
            //         setUserDaTrained('Data Access Training Completed'); // this line appears to matter
            //     }
            //     if (result === 'Update not needed') {
            //         message = 'Training status was already set to completed';
            //     }
            // } else {
            //     if (result === 'Updated') {
            //         message = `Successfully set training status to incomplete for ${teamMember.firstName} ${teamMember.lastName}`;
            //         setUserDaTrained('Data Access Training Not Completed'); // this line also appears to matter (based on empirical evidence)
            //     }
            //     if (result === 'Update not needed') {
            //         message = 'Training status was already set to incomplete';
            //     }
            // }
            // toastService.success(message);
            // getMenuItems(teamMember);
            // teamMember.statusArray = tempWorkArray;
            //
            // // what we want is to update the colDef (statusArray)
            // if (result === 'Updated') {
            //     dataRequestsService.updateDataRequest(dataRequest);
            //
            //     const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
            //         if (tm.id === teamMember.id) {
            //             return teamMember;
            //         }
            //         return tm;
            //     });
            //     formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
            // }
        });
    };

    const handleOnInviteToVm = async (teamMember) => {
        const invitation: IVirtualMachineAccessInvitation | null = await checkDisabledAccount(teamMember);

        if (!invitation) {
            return;
        }

        const confirmMsg = `Are you sure you want to grant "${teamMember.firstName} ${teamMember.lastName}" access to data request?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                setIsLoading(true);
                dataRequestsService
                    .inviteToVm(invitation)
                    .pipe(first(), finalize(handleCleanup))
                    .subscribe(
                        ({ invitation, dataRequest }) => {
                            // handleChange(dataRequest);
                            refreshScreen();
                        },
                        (error) => {
                            modalService.error(error);
                        }
                    );

                // let itemFound = false;
                // const tempWorkArray = teamMember.statusArray.map((status) => {
                //     if (status === 'Access to Data Request Denied' || status === 'Access to Data Request Pending') {
                //         itemFound = true;
                //         return 'Access to Data Request Granted';
                //     }
                //
                //     return status;
                // });
                //
                // if (!itemFound) {
                //     tempWorkArray?.push('Access to Data Request Granted');
                // }
                // teamMember.hasAccessToVirtualMachineResource = true;
                // getMenuItems(teamMember);
                // teamMember.statusArray = tempWorkArray;
            }
        });

        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        //
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const checkDisabledAccount = (teamMember): Promise<IVirtualMachineAccessInvitation | null> => {
        // Currently, we only check for the disabled status on the front end; other validations are handled by the back end.
        // Can we move this to back-end too in the future?

        return new Promise((resolve) => {
            const emailParam: IUserEmailParam = { emailAddress: teamMember.email };

            usersService
                .getUserByEmail(emailParam)
                .pipe(first())
                .toPromise()
                .then((user: IUser) => {
                    if (user.disabled) {
                        modalService.error(
                            `The Vivli account for user "${teamMember.email}" is disabled. Only users with active Vivli Accounts can be activated for access to this request.`
                        );
                        resolve(null);
                        return;
                    }

                    const invitation: IVirtualMachineAccessInvitation = {
                        dataRequestId: dataRequest.id,
                        email: teamMember.email,
                        InvitedById: user.userId,
                        teamMemberId: teamMember.teamMemberId
                    };

                    resolve(invitation);
                })
                .catch((error) => {
                    modalService.error(`Error fetching user: ${teamMember.email}`);
                    resolve(null);
                });
        });
    };

    const getMenuItems = (teamMember) => {
        const resultMenuItems: IDropdownMenuItem[] = [];

        if (onRemove && !teamMember.isLeadResearcher && userCanEdit) {
            resultMenuItems.push({
                title: 'Remove Team Member',
                onClick: () => handleOnRemove(teamMember),
                dataIdPrefix: 'remove-team-member'
            });
        }

        if (teamMember.adminApprovalStatus !== VmAdminApprovalStatusEnum.Approved && isVivliAdmin) {
            resultMenuItems.push({
                title: 'Grant Access to data',
                onClick: () => handleOnGrantDuaApproval(teamMember),
                dataIdPrefix: 'grant-dua-approval'
            });
        }

        //TODO PERMFE - redo with permissions+
        if (teamMember.adminApprovalStatus !== VmAdminApprovalStatusEnum.Disapproved && isVivliAdmin) {
            resultMenuItems.push({
                title: 'Deny Access to data',
                onClick: () => handleOnRevokeDuaApproval(teamMember),
                dataIdPrefix: 'deny-dua-approval'
            });
        }

        if ((onRevokeVmAccess || teamMember.hasAccessToVirtualMachineResource) && teamMember.userId !== user.userId) {
            resultMenuItems.push({
                title: 'Deny Access to data request',
                onClick: () => handleOnRevokeVmAccess(teamMember),
                dataIdPrefix: 'revoke-access'
            });
        }

        if (onInviteToVm || (!teamMember.hasAccessToVirtualMachineResource && teamMember.email !== user.email)) {
            resultMenuItems.push({
                title: 'Grant Access to data request',
                onClick: () => handleOnInviteToVm(teamMember),
                dataIdPrefix: 'invite-member'
            });
        }

        if (onMakeOwner || (!teamMember.isAdmin && teamMember.hasAccessToVirtualMachineResource)) {
            resultMenuItems.push({
                title: 'Transfer Administrator Role to Member',
                onClick: () => handleOnMakeOwner(teamMember),
                dataIdPrefix: 'transfer-administrator'
            });
        }

        if (teamMember.hasBeenInvitedToVMAccess && !teamMember.hasAccessToVirtualMachineResource) {
            resultMenuItems.push({
                title: 'Cancel Invite',
                onClick: () => handleOnCancelInvite(teamMember),
                dataIdPrefix: 'cancel-invite'
            });
        }

        if (isVivliAdmin && teamMember.userId?.length > 0) {
            resultMenuItems.push({
                title: 'Data Access Training Completed',
                onClick: () => onSetUnSetAccessTraining(true, teamMember),
                dataIdPrefix: 'set-access-training'
            });
            resultMenuItems.push({
                title: 'Data Access Training Not Completed',
                onClick: () => onSetUnSetAccessTraining(false, teamMember),
                dataIdPrefix: 'reset-access-training'
            });
        }

        return resultMenuItems;
    };

    const handleRenderResearcher = (teamMember: IResearchTeamMemberDetail) => {
        if (isActiveUserOrgAdmin() && !userCanManage) {
            return; //org admin cannot click on the row and see the popup
        }
        const newMember = createResearcher(ResearchTeamMemberRoleEnum.AdditionalResearcher);
        if (!draftMode) {
            const modalId = modalService.custom(
                <TeamMemberModalComponent
                    onSave={(teamMember) => {
                        modalService.dismiss(modalId);
                    }}
                    onCancel={() => modalService.dismiss(modalId)}
                    teamMember={teamMember}
                    dataRequest={dataRequest}
                />,
                {
                    style: {
                        width: '80%',
                        height: '80%',
                        overflowY: 'auto'
                    }
                }
            );
        } else {
            let fieldIndex = members.findIndex(
                (member) =>
                    (teamMember.id && member.id === teamMember.id) ||
                    (teamMember.teamMemberId && member.teamMemberId === teamMember.teamMemberId)
            );
            if (fieldIndex < 0) {
                //this is for old or corrupted data requests where the id is missing - so we match on name as before
                fieldIndex = members.findIndex(
                    (member) => member.firstName === teamMember.firstName && member.lastName === teamMember.lastName
                );
            }
            const memberPath = getMemberArrayPath(fieldIndex);
            const modalId = modalService.custom(
                <DraftTeamMemberModalComponent
                    teamMember={teamMember}
                    onSave={(updatedTeamMember) => {
                        modalService.dismiss(modalId);
                        formApi.setValue(memberPath, updatedTeamMember, { shouldDirty: true });
                        formApi.trigger();
                    }}
                    onDelete={(updatedTeamMember) => {
                        modalService.dismiss(modalId);
                        handleOnRemove(teamMember);
                    }}
                    onCancel={() => {
                        modalService.dismiss(modalId);
                    }}
                    dataRequest={dataRequest}
                    userCanEdit={draftMode}
                    userCanDelete={userCanDelete && !!onRemove && !teamMember.isAdmin}
                />,
                {
                    style: {
                        width: '80%',
                        height: '80%',
                        overflowY: 'auto'
                    }
                }
            );
        }
    };

    // filter members to display in grid cards
    // check for validation errors in draft mode - only for non-lead researchers and non-statisticians
    // because the others can't be saved with validation errors.
    const filteredMembers = useMemo(() => {
        if (draftMode) {
            const checkedMembers = members;
            checkedMembers?.map((mem, index) => {
                const fields = formApi.getValues('researchTeam.researchTeamDetails');
                const memberFields = fields[index];
                const memberFieldKeys = Object.keys(memberFields || {});
                const hasAnyInvalidField = memberFieldKeys.some((key) => {
                    const fieldState = formApi.getFieldState(`researchTeam.researchTeamDetails.${index}.${key}`);
                    return fieldState?.invalid;
                });
                if (hasAnyInvalidField) {
                    mem.hasValidationError = true;
                }
            });

            return checkedMembers.filter((mem) => !mem.isLeadResearcher && !mem.isStatistician);
        }
        return members;
    }, [members]);

    const columnDefs: ColDef[] = [
        {
            headerName: 'Name',
            filter: true,
            sortable: true,
            tooltipField: 'Name',
            headerTooltip: 'Name',
            minWidth: 150,
            valueGetter: ({ data }) => {
                return `${data.firstName} ${data.lastName}`;
            },
            cellRenderer: GridCellRendererEnum.GridCellErrorIcon,
            cellRendererParams: ({ data }) => ({
                showErrorIcon: data.hasValidationError
            })
        },
        {
            field: 'employerOrAffiliation',
            headerName: 'Affiliation',
            filter: true,
            sortable: true,
            headerTooltip: 'Affiliation',
            minWidth: 200,
            cellRenderer: GridCellRendererEnum.GridCellLongText
        },
        {
            field: 'country.name',
            headerName: 'Country',
            headerTooltip: 'Country',
            filter: true,
            sortable: true
        },
        {
            field: 'email',
            headerName: 'Email',
            headerTooltip: 'Email',
            filter: true,
            sortable: true,
            minWidth: 200,
            cellRenderer: GridCellRendererEnum.GridCellLongText
        },
        {
            field: 'rolesArray',
            headerName: 'Role(s)',
            headerTooltip: 'Role',
            sortable: false,
            minWidth: 210,
            cellStyle: { textAlign: 'left' },
            cellRenderer: GridCellRendererEnum.GridCellList
        },
        {
            field: 'statusArray',
            headerName: 'Status Details',
            filter: true,
            sortable: true,
            minWidth: 310,
            cellStyle: { textAlign: 'left' },
            cellRenderer: GridCellRendererEnum.GridCellList
        },
        {
            field: 'ellipsisMenu',
            headerName: '',
            sortable: true,
            maxWidth: 80,
            cellStyle: { textAlign: 'center' },
            // custom component renderer
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => ({
                component: <EllipsisDropdownMenuComponent dataIdPrefix={data.lastName} position={'top-end'} items={getMenuItems(data)} />
            }),
            hide: !isVivliAdmin || isAdminEditing || draftMode
        }
    ];

    return (
        <GridView
            rowData={filteredMembers}
            columns={columnDefs}
            fullWidthColumns={false}
            hideFloatingFilter={true}
            autoHeight={true}
            onRowClick={handleRenderResearcher}
            ignoreClickForFields={['ellipsisMenu']}
        />
    );
};
